import { Box3, BufferAttribute, BufferGeometry, Vector3 } from 'three'
import { GLB_INSTANCE_OWNED_GEOMETRY } from './disposeGlbClone'

const CAD_UV_SPAN = 2
const box = new Box3()
const size = new Vector3()

function uvSpanIsCadRange(geometry: BufferGeometry): boolean {
  const uv = geometry.attributes.uv
  if (!uv || uv.count === 0) return true
  let minU = Infinity
  let minV = Infinity
  let maxU = -Infinity
  let maxV = -Infinity
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i)
    const v = uv.getY(i)
    if (u < minU) minU = u
    if (v < minV) minV = v
    if (u > maxU) maxU = u
    if (v > maxV) maxV = v
  }
  return maxU - minU > CAD_UV_SPAN || maxV - minV > CAD_UV_SPAN
}

/**
 * three@0.160 BufferGeometry.copy() assigns `userData` by reference.
 * Flags written on a clone leak to the catalog cache and every other instance.
 */
export function isolateGeometryUserData(geometry: BufferGeometry): BufferGeometry {
  geometry.userData = { ...geometry.userData }
  return geometry
}

/**
 * CAD/STEP handles (e.g. 55532012835) keep logo UVs in inches (−8..19).
 * Repeating a catalog swatch on that range looks like a gold thread.
 * Furniture GLBs stay on authored 0..1 UVs.
 */
export function applyCatalogSwatchUvsIfNeeded(mesh: {
  geometry?: BufferGeometry
}): boolean {
  const geometry = mesh.geometry
  if (!geometry?.attributes?.position) return false
  const alreadyRemapped =
    Boolean(geometry.userData.__catalogSwatchUvs) && !uvSpanIsCadRange(geometry)
  if (alreadyRemapped) return true
  if (!uvSpanIsCadRange(geometry)) return false

  if (
    !geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] ||
    geometry.userData.__catalogSwatchUvs
  ) {
    mesh.geometry = isolateGeometryUserData(geometry.clone())
    mesh.geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] = true
  } else {
    isolateGeometryUserData(geometry)
  }
  const owned = mesh.geometry
  owned.computeBoundingBox()
  if (!owned.boundingBox) return false
  box.copy(owned.boundingBox)
  box.getSize(size)

  const pos = owned.attributes.position
  const nrm = owned.attributes.normal
  const uvs = new Float32Array(pos.count * 2)
  const sx = size.x || 1
  const sy = size.y || 1
  const sz = size.z || 1

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    const nx = Math.abs(nrm?.getX(i) ?? 0)
    const ny = Math.abs(nrm?.getY(i) ?? 0)
    const nz = Math.abs(nrm?.getZ(i) ?? 0)
    let u = 0
    let v = 0
    if (nx >= ny && nx >= nz) {
      u = (z - box.min.z) / sz
      v = (y - box.min.y) / sy
    } else if (ny >= nx && ny >= nz) {
      u = (x - box.min.x) / sx
      v = (z - box.min.z) / sz
    } else {
      u = (x - box.min.x) / sx
      v = (y - box.min.y) / sy
    }
    uvs[i * 2] = u
    uvs[i * 2 + 1] = v
  }

  owned.setAttribute('uv', new BufferAttribute(uvs, 2))
  owned.userData.__catalogSwatchUvs = true
  return true
}

export function zoneNamesMatch(a?: string, b?: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  const normalize = (value: string) => value.replace(/[\s._<>]/g, '').toLowerCase()
  return normalize(a) === normalize(b)
}
